// REC7-S4: Sharing belongs to the saved recipe; the list owns its indicators and legacy publication tiles.
import {
  isRecipeSharesShape,
  type RecipeSharesDto,
  type RecipeShareDto,
} from "./recipeShareModel.ts";
import { isOwnRecipePublicationsShape, type OwnLocationDto } from "./ownRecipePublishingModel.ts";
import { isRecipeOffersShape, type RecipeOfferDto } from "./networkRecipeOffersModel.ts";
import { sortByName } from "./nameSort.ts";

interface Reply {
  status: number;
  body: unknown;
}
interface Source {
  publicationId: string;
  menuDrinkId: string;
  channel: "locations" | "franchisees";
}
interface Overview {
  locations: OwnLocationDto[];
  own: RecipeOfferDto[];
  share: RecipeSharesDto;
  sources: Source[];
}
export interface RecipeSharingDeps {
  panel: HTMLElement;
  status: HTMLElement;
  formStatus: HTMLElement;
  fetch: () => Promise<{ own: Reply; share: Reply; sources: Reply }>;
  share: (menuDrinkId: string, body: Record<string, unknown>) => Promise<Reply>;
  withdraw: (publicationId: string) => Promise<Reply>;
  confirmWithdraw: (name: string) => Promise<boolean>;
  onChange: () => void;
  onShared: () => void;
  toast: (message: string) => void;
  isCurrent: () => boolean;
}
function object(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object";
}
function message(reply: Reply, fallback: string): string {
  return object(reply.body) && typeof reply.body["message"] === "string"
    ? reply.body["message"]
    : fallback;
}
function sourcesFrom(value: unknown): Source[] | null {
  if (!object(value) || !Array.isArray(value["sources"])) return null;
  const rows = value["sources"];
  return rows.every(
    (row) =>
      object(row) &&
      typeof row["publicationId"] === "string" &&
      typeof row["menuDrinkId"] === "string" &&
      (row["channel"] === "locations" || row["channel"] === "franchisees"),
  )
    ? (rows as Source[])
    : null;
}
export function createRecipeSharing(deps: RecipeSharingDeps) {
  let overview: Overview | null = null;
  let currentId: string | null = null;
  let busy = false;
  let generation = 0;
  let formGeneration = 0;
  function close(): void {
    formGeneration += 1;
    currentId = null;
    busy = false;
  }
  let selectedLocations = new Set<string>();
  let selectedPeers = new Set<string>();
  let toCorporate = false;
  let requestId: string | null = null;
  function setStatus(statusText: string): void {
    deps.status.textContent = statusText;
    deps.formStatus.textContent = statusText;
  }
  function reset(): void {
    generation += 1;
    close();
    overview = null;
    currentId = null;
    busy = false;
    requestId = null;
    deps.panel.replaceChildren();
    setStatus("");
  }
  function sourcesFor(id: string): Source[] {
    return overview?.sources.filter((source) => source.menuDrinkId === id) ?? [];
  }
  function indicators(id: string): string[] {
    if (overview === null) return [];
    const flags: string[] = [];
    for (const source of sourcesFor(id)) {
      const own = overview.own.find(
        (publication) => publication.publicationId === source.publicationId,
      );
      if (own !== undefined)
        flags.push(
          `Shared with ${own.locations.length} location${own.locations.length === 1 ? "" : "s"}`,
        );
      const share = overview.share.shares.find(
        (publication) =>
          publication.publicationId === source.publicationId && publication.state === "published",
      );
      if (share !== undefined) {
        if (share.recipients.length > 0)
          flags.push(
            `Shared with ${share.recipients.length} franchisee${share.recipients.length === 1 ? "" : "s"}`,
          );
        if (share.toFranchisor) flags.push("Submitted to corporate");
      }
    }
    return flags;
  }
  async function refresh(): Promise<void> {
    const gen = ++generation;
    const result = await deps.fetch();
    if (gen !== generation || !deps.isCurrent()) return;
    const sources = result.sources.status === 200 ? sourcesFrom(result.sources.body) : null;
    const own = result.own.body;
    const share = result.share.body;
    const publications = object(own) ? own["publications"] : null;
    const offers = { network: null, offers: publications };
    if (
      result.own.status !== 200 ||
      result.share.status !== 200 ||
      sources === null ||
      !isOwnRecipePublicationsShape(own) ||
      !isRecipeSharesShape(share) ||
      !isRecipeOffersShape(offers)
    ) {
      overview = null;
      setStatus("Sharing could not be loaded. Reopen Recipes to retry.");
      deps.onChange();
      return;
    }
    overview = { locations: own.locations, own: offers.offers, share, sources };
    setStatus("");
    deps.onChange();
  }
  function check(
    label: string,
    testId: string,
    value: string,
    checked: boolean,
    changed: (on: boolean) => void,
  ): HTMLElement {
    const row = document.createElement("label");
    row.className = "admin-discount-check";
    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = checked;
    input.value = value;
    input.dataset["testid"] = testId;
    input.dataset["pickName"] = label;
    input.addEventListener("change", () => {
      requestId = null;
      changed(input.checked);
    });
    row.append(input, document.createTextNode(` ${label}`));
    return row;
  }
  async function withdraw(publicationId: string, name: string): Promise<void> {
    if (busy) return;
    const formGen = formGeneration;
    const sourceId = currentId;
    const current = () => formGen === formGeneration && sourceId === currentId && deps.isCurrent();
    busy = true;
    try {
      const confirmed = await deps.confirmWithdraw(name);
      if (!current() || !confirmed) return;
      const result = await deps.withdraw(publicationId);
      if (!current()) return;
      if (result.status !== 200) {
        setStatus(message(result, "Could not withdraw this shared version."));
        return;
      }
      deps.toast("Shared version withdrawn.");
      await refresh();
      if (!current()) return;
      if (sourceId !== null) open(sourceId);
      deps.onShared();
    } finally {
      if (current()) busy = false;
    }
  }
  function publicationStatus(publication: RecipeShareDto | RecipeOfferDto): HTMLElement {
    const block = document.createElement("div");
    block.className = "admin-recipe-sharing-version";
    const heading = document.createElement("h3");
    heading.className = "admin-subsection-title";
    heading.textContent = "Shared version";
    block.append(heading);
    if ("recipients" in publication) {
      for (const recipient of publication.recipients) {
        const row = document.createElement("p");
        row.textContent = `${recipient.name}: ${recipient.state === "adopted" ? "Mapped" : recipient.state === "needs_mapping" ? "Needs mapping" : recipient.state === "declined" ? "Declined" : "Not answered"}`;
        block.append(row);
      }
      if (publication.toFranchisor) {
        const row = document.createElement("p");
        row.textContent = "Submitted to corporate";
        block.append(row);
        const proposal = publication.proposal;
        if (proposal !== undefined && proposal !== null) {
          const status = document.createElement("p");
          status.dataset["testid"] = "admin-recipe-proposal-status";
          status.textContent = `Corporate: ${proposal.status === "accepted" ? "Accepted" : proposal.status === "declined" ? "Declined" : proposal.status === "withdrawn" ? "Withdrawn" : "Pending"}`;
          block.append(status);
          if (proposal.note !== null) {
            const note = document.createElement("p");
            note.dataset["testid"] = "admin-recipe-proposal-note";
            note.textContent = `Note from corporate: ${proposal.note}`;
            block.append(note);
          }
        }
      }
    } else {
      const row = document.createElement("p");
      row.textContent = `${publication.locations.map((siteLocation) => siteLocation.name).join(", ")}: ${publication.state === "adopted" ? "Mapped" : publication.state === "needs_mapping" ? "Needs mapping" : publication.state === "declined" ? "Declined" : "Not answered"}`;
      block.append(row);
    }
    const button = document.createElement("button");
    button.type = "button";
    button.className = "btn-secondary btn-compact";
    button.dataset["testid"] = "admin-recipe-sharing-withdraw";
    button.textContent = "Withdraw shared version";
    button.addEventListener(
      "click",
      () => void withdraw(publication.publicationId, publication.name),
    );
    if (!("recipients" in publication) || publication.proposal?.status !== "accepted") block.append(button);
    return block;
  }
  function open(id: string): void {
    formGeneration += 1;
    busy = false;
    currentId = id;
    requestId = null;
    selectedLocations = new Set();
    selectedPeers = new Set();
    toCorporate = false;
    deps.panel.replaceChildren();
    if (overview === null) {
      const status = document.createElement("p");
      status.textContent = "Sharing could not be loaded. Reopen Recipes to retry.";
      deps.panel.append(status);
      return;
    }
    const activeOwn: RecipeOfferDto[] = [];
    const activeShares: RecipeShareDto[] = [];
    for (const source of sourcesFor(id)) {
      const own = overview.own.find(
        (publication) => publication.publicationId === source.publicationId,
      );
      if (own !== undefined) {
        activeOwn.push(own);
        for (const siteLocation of own.locations) selectedLocations.add(siteLocation.id);
      }
      const share = overview.share.shares.find(
        (publication) =>
          publication.publicationId === source.publicationId && publication.state === "published",
      );
      if (share !== undefined) {
        activeShares.push(share);
        for (const recipient of share.recipients)
          if (recipient.ref !== undefined) selectedPeers.add(recipient.ref);
        toCorporate ||= share.toFranchisor;
      }
    }
    const columns = document.createElement("div");
    columns.className = "admin-recipe-sharing-columns";
    const choices = document.createElement("div");
    const history = document.createElement("div");
    const group = (title: string): HTMLElement => {
      const section = document.createElement("div");
      section.className = "admin-recipe-sharing-group";
      const heading = document.createElement("h3");
      heading.className = "admin-subsection-title";
      heading.textContent = title;
      section.append(heading);
      choices.append(section);
      return section;
    };
    const ownGroup = group("Your locations");
    for (const siteLocation of sortByName(overview.locations, (entry) => entry.name))
      ownGroup.append(
        check(
          siteLocation.name,
          "admin-recipe-sharing-location",
          siteLocation.id,
          selectedLocations.has(siteLocation.id),
          (on) => {
            if (on) selectedLocations.add(siteLocation.id);
            else selectedLocations.delete(siteLocation.id);
          },
        ),
      );
    if (overview.share.inNetwork) {
      const peerGroup = group("Other franchisees");
      for (const peer of sortByName(overview.share.peers, (entry) => entry.name))
        peerGroup.append(
          check(
            peer.name,
            "admin-recipe-sharing-peer",
            peer.ref,
            selectedPeers.has(peer.ref),
            (on) => {
              if (on) selectedPeers.add(peer.ref);
              else selectedPeers.delete(peer.ref);
            },
          ),
        );
      choices.append(
        check(
          "Submit to corporate",
          "admin-recipe-sharing-corporate",
          "corporate",
          toCorporate,
          (on) => {
            toCorporate = on;
          },
        ),
      );
    }
    for (const publication of [...activeOwn, ...activeShares])
      history.append(publicationStatus(publication));
    columns.append(choices, history);
    deps.panel.append(columns);
  }
  async function save(): Promise<boolean> {
    if (busy || currentId === null || overview === null) return false;
    busy = true;
    const formGen = formGeneration;
    const sourceId = currentId;
    const current = () => formGen === formGeneration && sourceId === currentId && deps.isCurrent();
    requestId ??= crypto.randomUUID();
    const result = await deps.share(sourceId, {
      requestId,
      locationIds: [...selectedLocations],
      recipientRefs: [...selectedPeers],
      toFranchisor: toCorporate,
    });
    if (!current()) return false;
    busy = false;
    if (result.status !== 201) {
      setStatus(message(result, "Could not share this recipe."));
      return false;
    }
    deps.toast("Recipe shared.");
    await refresh();
    if (!current()) return false;
    deps.onShared();
    return true;
  }
  function legacyRows(): HTMLElement[] {
    if (overview === null) return [];
    const linked = new Set(overview.sources.map((source) => source.publicationId));
    return sortByName(
      overview.share.shares.filter(
        (publication) =>
          !linked.has(publication.publicationId) && publication.state === "published",
      ),
      (entry) => entry.name,
    ).map((publication) => {
      const row = document.createElement("div");
      row.className = "admin-recipe-legacy-tile";
      row.dataset["recipeName"] = publication.name;
      row.dataset["recipeCategory"] = publication.category;
      row.dataset["testid"] = "admin-recipe-shared-version";
      const title = document.createElement("h3");
      title.textContent = publication.name;
      const origin = document.createElement("p");
      origin.className = "admin-recipe-origin";
      origin.textContent = "Your recipe";
      const category = document.createElement("p");
      category.textContent = publication.category;
      const details = document.createElement("details");
      const summary = document.createElement("summary");
      summary.textContent = "Sharing";
      details.append(summary, publicationStatus(publication));
      row.append(title, category, origin, details);
      return row;
    });
  }
  return { refresh, reset, close, open, save, indicators, legacyRows };
}
